// How a thread row is labelled. Split out of the component so `node --test` can cover
// the words without jsdom — same reason `emptyStates.ts` exists.
//
// Direction is a field the server already classified. These helpers must not look at a
// subject prefix to decide sent vs received; that is how a `[Gong In]` reply would be
// painted as outbound the moment somebody "cleaned up" the label.

export function directionLabel(direction: string, isAutoreply: boolean): string {
  if (direction === 'received' && isAutoreply) return 'Auto-reply';
  if (direction === 'received') return 'Received';
  if (direction === 'sent') return 'Sent';
  return direction;
}

export function channelLabel(channel: string): string {
  if (channel === 'linkedin') return 'LinkedIn';
  if (channel === 'email') return 'Email';
  if (channel === 'call') return 'Call';
  return 'Activity';
}

// The line a bubble leads with. A missing subject is activity, not an empty email —
// dressing a call as a conversation is the thing the brief forbade.
export function threadHeadline(message: {
  subject: string | null;
  channel: string;
  day: string;
}): { text: string; isActivity: boolean } {
  if (message.subject) return { text: message.subject, isActivity: false };
  return { text: `${channelLabel(message.channel)} · ${message.day}`, isActivity: true };
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// `occurred_at` is Pacific wall-clock from Snowflake, not a UTC instant. Parsing it
// with `new Date(...)` would shift 23% of rows onto the wrong day, which is the
// same bug the connector already documents on raw timestamps.
export function formatThreadStamp(occurredAt: string, day: string): string {
  const match = occurredAt.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!match) return day;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const date = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const weekday = WEEKDAYS[new Date(Date.UTC(year, month - 1, date)).getUTCDay()];
  const monthLabel = MONTHS[month - 1];
  const hour12 = hour % 12 || 12;
  const suffix = hour < 12 ? 'am' : 'pm';
  const minuteText = String(minute).padStart(2, '0');
  return `${weekday} ${date} ${monthLabel}, ${hour12}:${minuteText}${suffix}`;
}

export function threadWho(message: {
  direction: string;
  author: string;
  contact_name: string | null;
}): string {
  if (message.direction === 'sent') {
    return message.contact_name ? `${message.author} → ${message.contact_name}` : message.author;
  }
  return message.contact_name || 'Them';
}

// An expander on a call, or on an empty DESCRIPTION, is a blank that looks like a bug.
// The server already nulls a call's body; this is the page's own check.
export function hasExpandableBody(
  body: string | null | undefined,
  channel?: string,
): boolean {
  if (channel === 'call') return false;
  return Boolean(body && body.trim());
}
